import { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import { NAVY, GOLD } from '../utils/constants';
import { FileText, FolderOpen, AlertTriangle } from 'lucide-react';

// ─── Date helpers ─────────────────────────────────────────────────────────────
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const ordinal = (n) => { const s=['th','st','nd','rd']; const v=n%100; return n+(s[(v-20)%10]||s[v]||s[0]); };
// Local-timezone today (toISOString is UTC — before 05:30 IST it returns yesterday)
const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const fmtDateWords = (iso) => { if (!iso) return ''; const [y,m,d]=iso.split('-').map(Number); return `${ordinal(d)} ${MONTHS[m-1]} ${y}`; };
const isoToDisplay = (iso) => { if (!iso) return ''; const [y,m,d]=iso.split('-'); return `${d}-${m}-${y}`; };
const shiftISO = (iso, days) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ─── Column aliases (all lowercase after normalization) ───────────────────────
const normalizeHeader = (h) => String(h||'').trim().toLowerCase().replace(/\s+/g,' ');

const COL_ALIASES = [
  { key: 'adults',               aliases: ['adults','adult'] },
  { key: 'children',             aliases: ['child','children','ch'] },
  { key: 'deck',                 aliases: ['deck'] },
  { key: 'booking_name',         aliases: ['booking name'] },
  { key: 'partner_name',         aliases: ['partner name'] },
  { key: 'guest_phone',          aliases: ['phone number','phone'] },
  { key: 'total_amount',         aliases: ['total'] },
  { key: 'tac',                  aliases: ['tac'] },
  { key: 'net_total',            aliases: ['net total'] },
  { key: 'payment_status',       aliases: ['payment status'] },
  { key: 'razorpay_amount',      aliases: ['razorpay'] },
  { key: 'cash_amount',          aliases: ['cash'] },
  { key: 'icici_amount',         aliases: ['icici pos','icici','icicip os'] },
  { key: 'bank_transfer_amount', aliases: ['bank transfer'] },
  { key: 'gpay_amount',          aliases: ['gpay'] },
  { key: 'notes',                aliases: ['notes'] },
  { key: 'driver_commission',    aliases: ['driver commission'] },
];

function matchHeader(h) {
  const norm = normalizeHeader(h);
  for (const { key, aliases } of COL_ALIASES) {
    if (aliases.includes(norm)) return key;
  }
  return null;
}

// ─── Sheet name matching ──────────────────────────────────────────────────────
const CRUISE_SHEET_PATTERNS = [
  { type: 'Sunset Cruise',  patterns: ['sunset cruise','sunset'] },
  { type: 'Sundown Cruise', patterns: ['sundown cruise','sundown'] },
  { type: 'Dinner Cruise',  patterns: ['dinner cruise','dinner'] },
];

function findSheetName(workbook, cruiseType) {
  const entry = CRUISE_SHEET_PATTERNS.find(e => e.type === cruiseType);
  if (!entry) return null;
  for (const name of workbook.SheetNames) {
    if (entry.patterns.includes(name.trim().toLowerCase())) return name;
  }
  return null;
}

// ─── Sheet parser ─────────────────────────────────────────────────────────────
function parseSheetBookings(worksheet, cruiseType) {
  const raw = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
  if (!raw?.length) return [];

  let headerIdx = -1;
  let colMap = {};
  for (let i = 0; i < Math.min(raw.length, 25); i++) {
    const row = raw[i];
    const tmp = {};
    let found = 0;
    for (let j = 0; j < row.length; j++) {
      const key = matchHeader(String(row[j]));
      if (key) { tmp[key] = j; found++; }
    }
    if (found >= 3 && 'booking_name' in tmp) { headerIdx = i; colMap = tmp; break; }
  }
  if (headerIdx === -1) return [];

  const getNum = (row, key) => (colMap[key] !== undefined ? Number(row[colMap[key]]) || 0 : 0);
  const getStr = (row, key) => (colMap[key] !== undefined ? String(row[colMap[key]] ?? '').trim() : '');

  const bookings = [];
  for (let i = headerIdx + 1; i < raw.length; i++) {
    const row = raw[i];
    const bookingName = getStr(row, 'booking_name');
    const adults = getNum(row, 'adults');

    // Skip rows where booking name is empty AND adults is 0
    if (!bookingName && adults === 0) continue;
    // Skip header/summary rows
    const lower = bookingName.toLowerCase();
    if (lower === 'booking name' || lower === 'total' || lower === 'grand total' || lower === 'subtotal') continue;

    bookings.push({
      cruise_type: cruiseType,
      booking_name: bookingName,
      adults,
      children:             getNum(row, 'children'),
      deck:                 getStr(row, 'deck') || null,
      partner_name:         getStr(row, 'partner_name') || null,
      guest_phone:          getStr(row, 'guest_phone') || null,
      total_amount:         getNum(row, 'total_amount'),
      tac:                  getNum(row, 'tac'),
      net_total:            getNum(row, 'net_total'),
      payment_status:       getStr(row, 'payment_status') || null,
      razorpay_amount:      getNum(row, 'razorpay_amount'),
      cash_amount:          getNum(row, 'cash_amount'),
      icici_amount:         getNum(row, 'icici_amount'),
      bank_transfer_amount: getNum(row, 'bank_transfer_amount'),
      gpay_amount:          getNum(row, 'gpay_amount'),
      notes:                getStr(row, 'notes') || null,
      driver_commission:    getNum(row, 'driver_commission'),
    });
  }
  return bookings;
}

// ─── Import logic ─────────────────────────────────────────────────────────────
async function doImport(dateISO, bookings) {
  const displayDate = isoToDisplay(dateISO);
  const BATCH = 50;

  // 1. Delete existing bookings for the date
  const { data: existing } = await supabase.from('bookings').select('id').eq('date', displayDate);
  if (existing?.length) {
    const ids = existing.map(b => b.id);
    await Promise.all([
      supabase.from('tracker_entries').delete().in('booking_id', ids),
      supabase.from('booking_references').delete().in('booking_id', ids),
      supabase.from('seating_assignments').delete().in('booking_id', ids),
    ]);
    await supabase.from('bookings').delete().eq('date', displayDate);
  }

  // 2. Fetch cruise code lookup
  const { data: cruiseSettings } = await supabase.from('cruise_settings').select('cruise_name, cruise_code');
  const codeMap = {};
  (cruiseSettings || []).forEach(c => { codeMap[c.cruise_name] = c.cruise_code; });

  // 2b. Partner name → id map. Exact (case/space-insensitive) and UNAMBIGUOUS
  // matches only — a duplicate partner name maps to null rather than guessing,
  // since a wrong link attributes money to the wrong partner.
  // Archived partners must not be resolvable from a spreadsheet name either.
  const { data: partnerRows } = await supabase.from('partners').select('id, name').is('archived_at', null);
  const partnerIdByName = new Map();
  (partnerRows || []).forEach(p => {
    const k = (p.name || '').trim().toLowerCase();
    if (!k) return;
    partnerIdByName.set(k, partnerIdByName.has(k) ? null : p.id);
  });

  // 3. Insert bookings in batches, collect inserted IDs in order
  const bookingRecords = bookings.map(b => ({
    date:                 displayDate,
    cruise_type:          b.cruise_type,
    deck:                 b.deck || null,
    booking_name:         b.booking_name,
    adults:               b.adults,
    children:             b.children || 0,
    tour_leaders:         0,
    partner_name:         b.partner_name || null,
    partner_id:           b.partner_name ? (partnerIdByName.get(b.partner_name.trim().toLowerCase()) ?? null) : null,
    guest_phone:          b.guest_phone || null,
    partner_phone:        null,
    total_amount:         b.total_amount || 0,
    tac:                  b.tac || 0,
    net_total:            b.net_total || ((b.total_amount || 0) - (b.tac || 0)),
    payment_status:       b.payment_status || null,
    notes:                b.notes || null,
    razorpay_amount:      b.razorpay_amount || 0,
    cash_amount:          b.cash_amount || 0,
    icici_amount:         b.icici_amount || 0,
    bank_transfer_amount: b.bank_transfer_amount || 0,
    gpay_amount:          b.gpay_amount || 0,
  }));

  const insertedIds = [];
  for (let i = 0; i < bookingRecords.length; i += BATCH) {
    const { data, error } = await supabase.from('bookings').insert(bookingRecords.slice(i, i + BATCH)).select('id');
    if (error) throw error;
    (data || []).forEach(r => insertedIds.push(r.id));
  }

  // 4. Create tracker entries for bookings that have driver_commission
  const trackerRecords = [];
  insertedIds.forEach((id, idx) => {
    const b = bookings[idx];
    if (b.driver_commission) {
      trackerRecords.push({ booking_id: id, driver_commission: b.driver_commission || 0 });
    }
  });
  for (let i = 0; i < trackerRecords.length; i += BATCH) {
    await supabase.from('tracker_entries').insert(trackerRecords.slice(i, i + BATCH));
  }

  // 5. Generate booking references (efficient bulk approach)
  const { data: existingRefs } = await supabase.from('booking_references').select('reference_number');
  const maxSeqs = {};
  (existingRefs || []).forEach(r => {
    const parts = (r.reference_number || '').split('-');
    if (parts.length >= 3) {
      const key = `${parts[0]}||${parts[2]}`;
      maxSeqs[key] = Math.max(maxSeqs[key] || 0, parseInt(parts[1], 10) || 0);
    }
  });
  const [dd, mm, yyyy] = displayDate.split('-');
  const refSuffix = `${dd}${mm}${yyyy.slice(2)}`;
  const nextSeqs = { ...maxSeqs };
  const refsToInsert = [];
  insertedIds.forEach((id, idx) => {
    const cruiseCode = codeMap[bookings[idx].cruise_type];
    if (!cruiseCode) return;
    const seqKey = `${cruiseCode}||${refSuffix}`;
    const seq = (nextSeqs[seqKey] || 0) + 1;
    nextSeqs[seqKey] = seq;
    refsToInsert.push({ booking_id: id, reference_number: `${cruiseCode}-${String(seq).padStart(3, '0')}-${refSuffix}` });
  });
  for (let i = 0; i < refsToInsert.length; i += BATCH) {
    await supabase.from('booking_references').insert(refsToInsert.slice(i, i + BATCH));
  }

  return insertedIds.length;
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const card = {
  background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14,
  padding: '22px 24px', marginBottom: 16, boxShadow: '0 1px 6px rgba(10,22,40,0.06)',
};
const stepLabelStyle = {
  fontSize: '0.68rem', fontWeight: 700, color: 'var(--color-ink-muted)',
  textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 14,
  display: 'flex', alignItems: 'center', gap: 8,
};
const thStyle = {
  padding: '8px 12px', fontWeight: 700, fontSize: '0.72rem', color: 'var(--color-ink-secondary)',
  textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left',
  whiteSpace: 'nowrap', background: 'var(--color-sunken)', borderBottom: '2px solid var(--color-border)',
};
const tdStyle = {
  padding: '7px 12px', fontSize: '0.82rem', color: '#2a3a5a',
  borderBottom: '1px solid var(--color-border-subtle)', verticalAlign: 'middle',
};
const arrowBtn = {
  background: NAVY, border: 'none', borderRadius: 7,
  width: 34, height: 34, cursor: 'pointer', fontWeight: 700, color: '#fff',
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  fontSize: '1.1rem', lineHeight: 1,
};

function StepBadge({ n, done }) {
  return (
    <div style={{
      width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
      background: done ? GOLD : NAVY, color: '#fff',
      fontSize: '0.7rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {done ? '✓' : n}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function ImportData({ onNavigate }) {
  const [dateISO, setDateISO]           = useState(todayISO);
  const [fileName, setFileName]         = useState('');
  const [parsedBookings, setParsed]     = useState(null);
  const [warnings, setWarnings]         = useState([]);
  const [parsing, setParsing]           = useState(false);
  const [isDragOver, setIsDragOver]     = useState(false);
  const [importing, setImporting]       = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError]   = useState('');
  const fileInputRef = useRef(null);

  const resetFileState = () => { setParsed(null); setFileName(''); setWarnings([]); setImportError(''); };

  const handleDateChange = (newISO) => { setDateISO(newISO); resetFileState(); setImportResult(null); };

  const processFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setImportError('Please upload an .xlsx file.'); return;
    }
    setFileName(file.name);
    setParsing(true);
    setImportError('');
    setParsed(null);
    setWarnings([]);
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(new Uint8Array(buffer), { type: 'array' });
      const allBookings = [];
      const warns = [];
      for (const { type } of CRUISE_SHEET_PATTERNS) {
        const sheetName = findSheetName(wb, type);
        if (!sheetName) { warns.push(`Sheet for "${type}" not found — skipped.`); continue; }
        allBookings.push(...parseSheetBookings(wb.Sheets[sheetName], type));
      }
      setParsed(allBookings);
      setWarnings(warns);
    } catch (e) {
      setImportError('Failed to read file: ' + e.message);
    } finally {
      setParsing(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setIsDragOver(false);
    processFile(e.dataTransfer.files[0]);
  }, [processFile]);

  const handleFileInput = useCallback((e) => {
    processFile(e.target.files[0]);
    e.target.value = '';
  }, [processFile]);

  const handleImport = async () => {
    if (!parsedBookings?.length) return;
    setImporting(true); setImportError('');
    try {
      const count = await doImport(dateISO, parsedBookings);
      setImportResult({ count, dateISO });
    } catch (e) {
      setImportError('Import failed: ' + (e.message || String(e)));
    } finally {
      setImporting(false);
    }
  };

  const sunsetCount  = parsedBookings?.filter(b => b.cruise_type === 'Sunset Cruise').length || 0;
  const sundownCount = parsedBookings?.filter(b => b.cruise_type === 'Sundown Cruise').length || 0;
  const dinnerCount  = parsedBookings?.filter(b => b.cruise_type === 'Dinner Cruise').length || 0;
  const totalCount   = (parsedBookings || []).length;
  const hasPreview   = parsedBookings !== null;
  const hasFile      = !!fileName;

  // ── Success screen ────────────────────────────────────────────────────────
  if (importResult) {
    return (
      <div>
        <div className="back-row">
          <button className="back-btn" onClick={() => onNavigate('operations')}>← Operations</button>
        </div>
        <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: NAVY, marginBottom: 4 }}>Import Data</h2>
        <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', marginBottom: 28 }}>Import daily booking data from Excel tracker files.</p>

        <div style={{ ...card, textAlign: 'center', padding: '44px 24px' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', background: '#e8f5e9',
            border: '2px solid #4caf50', display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 18px', fontSize: '1.6rem',
          }}>✓</div>
          <div style={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY, marginBottom: 6 }}>
            Successfully imported {importResult.count} bookings
          </div>
          <div style={{ fontSize: '0.88rem', color: 'var(--color-ink-muted)', marginBottom: 32 }}>
            for {fmtDateWords(importResult.dateISO)}
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={() => onNavigate('daily-tracker', isoToDisplay(importResult.dateISO))}
              style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' }}
            >
              View in Tracker
            </button>
            <button
              onClick={() => { setImportResult(null); resetFileState(); }}
              style={{ background: 'none', border: '1px solid var(--color-border-strong)', borderRadius: 8, padding: '10px 22px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', color: 'var(--color-ink-secondary)' }}
            >
              Import Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main import screen ─────────────────────────────────────────────────────
  return (
    <div>
      <div className="back-row">
        <button className="back-btn" onClick={() => onNavigate('operations')}>← Operations</button>
      </div>
      <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: NAVY, marginBottom: 4 }}>Import Data</h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', marginBottom: 28 }}>Import daily booking data from Excel tracker files.</p>

      {/* ── Step 1: Date ─────────────────────────────────────────────────── */}
      <div style={card}>
        <div style={stepLabelStyle}>
          <StepBadge n={1} done={false} />
          Select the date for this import
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button style={arrowBtn} onClick={() => handleDateChange(shiftISO(dateISO, -1))}>←</button>
          <input
            type="date"
            value={dateISO}
            onChange={e => handleDateChange(e.target.value)}
            style={{ border: '1px solid var(--color-border-strong)', borderRadius: 7, padding: '7px 11px', fontSize: '0.88rem', color: NAVY, fontWeight: 600, outline: 'none', cursor: 'pointer' }}
          />
          <button style={arrowBtn} onClick={() => handleDateChange(shiftISO(dateISO, 1))}>→</button>
          <span style={{ fontSize: '1rem', fontWeight: 700, color: NAVY }}>{fmtDateWords(dateISO)}</span>
        </div>
      </div>

      {/* ── Step 2: Upload ───────────────────────────────────────────────── */}
      <div style={card}>
        <div style={stepLabelStyle}>
          <StepBadge n={2} done={hasFile && hasPreview} />
          Upload Excel Tracker File
        </div>
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${isDragOver ? GOLD : '#c8c0b0'}`,
            borderRadius: 10, padding: '28px 24px', textAlign: 'center', cursor: 'pointer',
            background: isDragOver ? '#fffbf0' : '#fafaf8',
            transition: 'border-color 0.15s, background 0.15s',
          }}
        >
          <input ref={fileInputRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleFileInput} />
          {fileName ? (
            <>
              <div style={{ marginBottom: 6 }}><FileText size={24} strokeWidth={1.75} aria-hidden="true" /></div>
              <div style={{ fontWeight: 700, color: NAVY, fontSize: '0.9rem' }}>{fileName}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-ink-muted)', marginTop: 4 }}>Click to change file</div>
            </>
          ) : (
            <>
              <div style={{ marginBottom: 6 }}><FolderOpen size={24} strokeWidth={1.75} aria-hidden="true" /></div>
              <div style={{ fontWeight: 600, color: 'var(--color-ink-secondary)', fontSize: '0.9rem' }}>Drag and drop an .xlsx file here</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-ink-muted)', marginTop: 4 }}>or click to browse</div>
            </>
          )}
        </div>
        {parsing && (
          <div style={{ marginTop: 10, fontSize: '0.82rem', color: 'var(--color-ink-muted)' }}>Reading file…</div>
        )}
        {importError && !hasPreview && (
          <div style={{ marginTop: 10, fontSize: '0.82rem', color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '8px 12px' }}>
            {importError}
          </div>
        )}
      </div>

      {/* ── Warnings ─────────────────────────────────────────────────────── */}
      {warnings.length > 0 && (
        <div style={{ background: '#fffbf0', border: '1px solid #f5e6a0', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
          {warnings.map((w, i) => (
            <div key={i} style={{ fontSize: '0.82rem', color: '#92600a', display: 'flex', gap: 8 }}>
              <span style={{ display: 'flex', flexShrink: 0 }}><AlertTriangle size={16} strokeWidth={1.75} aria-hidden="true" /></span><span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Step 3: Preview ──────────────────────────────────────────────── */}
      {hasPreview && (
        <div style={card}>
          <div style={stepLabelStyle}>
            <StepBadge n={3} done={false} />
            Preview
          </div>
          {totalCount === 0 ? (
            <div style={{ fontSize: '0.85rem', color: 'var(--color-ink-muted)' }}>No bookings found in this file.</div>
          ) : (
            <>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#2a3a5a', marginBottom: 14 }}>
                {totalCount} bookings found
                {' '}
                <span style={{ fontWeight: 400, color: 'var(--color-ink-secondary)' }}>
                  ({sunsetCount} Sunset, {sundownCount} Sundown, {dinnerCount} Dinner)
                </span>
              </div>
              <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid var(--color-border)', maxHeight: 380, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Cruise','Deck','Booking Name','Adults','Children','Total','Payment Status'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parsedBookings.map((b, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafaf8' }}>
                        <td style={{ ...tdStyle, fontSize: '0.78rem', color: 'var(--color-ink-secondary)' }}>{b.cruise_type}</td>
                        <td style={tdStyle}>{b.deck || '—'}</td>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{b.booking_name}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{b.adults}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{b.children || 0}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {b.total_amount ? `₹${Number(b.total_amount).toLocaleString('en-IN')}` : '—'}
                        </td>
                        <td style={tdStyle}>{b.payment_status || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Step 4: Confirm ──────────────────────────────────────────────── */}
      {hasPreview && totalCount > 0 && (
        <div style={card}>
          <div style={stepLabelStyle}>
            <StepBadge n={4} done={false} />
            Confirm Import
          </div>
          <div style={{ background: '#fff8f0', border: '1px solid #fcd9aa', borderRadius: 8, padding: '12px 16px', marginBottom: 18, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ flexShrink: 0, display: 'flex' }}><AlertTriangle size={16} strokeWidth={1.75} aria-hidden="true" /></span>
            <div style={{ fontSize: '0.85rem', color: '#7a3a10', lineHeight: 1.6 }}>
              <strong>This will replace all existing bookings for {fmtDateWords(dateISO)}.</strong>
              {' '}This cannot be undone.
            </div>
          </div>
          {importError && (
            <div style={{ fontSize: '0.82rem', color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '8px 12px', marginBottom: 14 }}>
              {importError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={handleImport}
              disabled={importing}
              style={{
                background: importing ? '#8a9ab0' : NAVY, color: '#fff', border: 'none',
                borderRadius: 8, padding: '10px 24px', fontSize: '0.9rem', fontWeight: 700,
                cursor: importing ? 'not-allowed' : 'pointer', transition: 'background 0.15s',
              }}
            >
              {importing ? 'Importing…' : `Import ${totalCount} Bookings`}
            </button>
            <button
              onClick={() => { resetFileState(); if (fileInputRef.current) fileInputRef.current.value = ''; }}
              disabled={importing}
              style={{ background: 'none', border: '1px solid var(--color-border-strong)', borderRadius: 8, padding: '10px 22px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', color: 'var(--color-ink-secondary)' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
